// @flow
/* global SETTINGS: false */
import DocumentTitle from "react-document-title"
import React from "react"
import Card from "@material-ui/core/Card"
import PropTypes from "prop-types"
import type { Dispatch } from "redux"
import { connect } from "react-redux"
import _ from "lodash"
import R from "ramda"
import Alert from "react-bootstrap/lib/Alert"

import ProgramEnrollmentDialog from "../components/ProgramEnrollmentDialog"
import Loader from "../components/Loader"
import { FETCH_SUCCESS, FETCH_PROCESSING, FETCH_FAILURE } from "../actions"
import { fetchDashboard, clearDashboard } from "../actions/dashboard"
import { addProgramEnrollment } from "../actions/programs"
import { COUPON_CONTENT_TYPE_COURSE, TOAST_FAILURE } from "../constants"
import {
  setToastMessage,
  setConfirmSkipDialogVisibility,
  setDocsInstructionsVisibility,
  setCouponNotificationVisibility,
  setEnrollCourseDialogVisibility,
  setCalculatePriceDialogVisibility,
  setExamEnrollmentDialogVisibility,
  setSelectedExamCouponCourse,
  setEnrollSelectedCourseRun,
  showDialog,
  hideDialog,
  setShowExpandedCourseStatus,
  setEnrollProgramDialogError,
  setEnrollProgramDialogVisibility,
  setEnrollSelectedProgram
} from "../actions/ui"
import { showEnrollPayLaterSuccessMessage } from "../actions/course_enrollments"
import CourseListCard from "../components/dashboard/CourseListCard"
import DashboardUserCard from "../components/dashboard/DashboardUserCard"
import ErrorMessage from "../components/ErrorMessage"
import LearnersInProgramCard from "../components/LearnersInProgramCard"
import ProgressWidget from "../components/ProgressWidget"
import { clearCoupons } from "../actions/coupons"
import { attachCoupon, setRecentlyAttachedCoupon } from "../actions/coupons"
import { COURSE_EMAIL_TYPE } from "../components/email/constants"
import { COURSE_TEAM_EMAIL_CONFIG } from "../components/email/lib"
import { withEmailDialog } from "../components/email/hoc"
import CouponNotificationDialog from "../components/CouponNotificationDialog"
import CourseEnrollmentDialog from "../components/CourseEnrollmentDialog"
import SocialAuthReauthenticateDialog from "../components/SocialAuthReauthenticateDialog"
import { getOwnDashboard } from "../reducers/util"
import { actions } from "../lib/redux_rest"
import { gradeDetailPopupKey } from "../components/dashboard/courses/Grades"

import type { UIState } from "../reducers/ui"
import type { DashboardState, ProgramLearners } from "../flow/dashboardTypes"
import type { AllEmailsState } from "../flow/emailTypes"
import type {
  AvailableProgram,
  AvailableProgramsState
} from "../flow/enrollmentTypes"
import type { CouponsState } from "../reducers/coupons"
import type { ProfileGetResult } from "../flow/profileTypes"
import type { Course, CourseRun, Program } from "../flow/programTypes"
import type { Coupon } from "../flow/couponTypes"
import type { RestState } from "../flow/restTypes"
import type { ExamEnrollmentResponse } from "../flow/enrollmentTypes"
import ExamEnrollmentDialog from "../components/dashboard/ExamEnrollmentDialog"

const isFinishedProcessing = R.contains(R.__, [FETCH_SUCCESS, FETCH_FAILURE])

export type GradeType = "COURSE_GRADE" | "EXAM_GRADE"
export const COURSE_GRADE: GradeType = "COURSE_GRADE"
export const EXAM_GRADE: GradeType = "EXAM_GRADE"

class DashboardPage extends React.Component {
  static contextTypes = {
    router: PropTypes.object.isRequired
  }

  props: {
    coupons: CouponsState,
    profile: ProfileGetResult,
    currentProgramEnrollment: AvailableProgram,
    programs: AvailableProgramsState,
    dashboard: DashboardState,
    programLearners: RestState<ProgramLearners>,
    dispatch: Dispatch,
    ui: UIState,
    email: AllEmailsState,
    location: Object,
    openEmailComposer: (emailType: string, emailOpenParams: any) => void
  }

  componentDidMount() {
    this.updateRequirements()
  }

  componentDidUpdate() {
    this.updateRequirements()
  }

  componentWillUnmount() {
    const { dispatch, programLearners } = this.props
    dispatch(clearDashboard(SETTINGS.user.username))

    _.forEach(R.keys(programLearners), id =>
      dispatch(actions.programLearners.clear(id))
    )
    dispatch(clearCoupons())
  }

  openCourseContactDialog = (course: Course, canContactCourseTeam: boolean) => {
    const { openEmailComposer } = this.props
    if (canContactCourseTeam) {
      openEmailComposer(COURSE_EMAIL_TYPE, course)
    }
  }

  updateRequirements = () => {
    this.fetchDashboard()
    this.fetchProgramLearners()
    this.handleCoupon()
  }

  fetchDashboard() {
    const { dashboard, dispatch } = this.props
    if (dashboard.fetchStatus === undefined) {
      dispatch(fetchDashboard(SETTINGS.user.username)).catch(() => {
        /* Promise rejected */
      })
    }
  }

  fetchProgramLearners() {
    const { programLearners, dispatch } = this.props
    const program = this.getCurrentlyEnrolledProgram()
    if (
      program !== undefined &&
      R.pathEq([program.id, "getStatus"], undefined, programLearners)
    ) {
      dispatch(actions.programLearners.get(program.id)).catch(() => {
        /* Promise rejected */
      })
    }
  }

  handleCoupon = () => {
    const {
      coupons,
      dispatch,
      location: { query }
    } = this.props

    if (!query.coupon) {
      // If there's no coupon code in the URL query parameters,
      // there's nothing to do.
      return
    }

    if (coupons.fetchPostStatus !== undefined) {
      // If we've already launched a POST request to attach this coupon
      // to this user, don't launch another one.
      return
    }

    if (
      coupons.fetchGetStatus === FETCH_PROCESSING ||
      coupons.fetchGetStatus === undefined
    ) {
      /*
      Abort to avoid the following race condition:

        1. launch first fetchCoupons() API request
        2. launch attachCoupon() API request
        3. attachCoupon() returns, launch second fetchCoupons() API request
        4. second fetchCoupons() returns, updates Redux store with accurate information
        5. first fetchCoupons() finally returns, updates Redux store with stale information

      Ideally, it would be nice to abort the first fetchCoupons() API request
      in this case, but fetches can't be aborted. Instead, we will abort
      this function (by returning early), and rely on being called again in the
      future.
      */
      return
    }

    dispatch(attachCoupon(query.coupon)).then(
      result => {
        this.setRecentlyAttachedCoupon(result.coupon)
        this.setCouponNotificationVisibility(true)
        this.context.router.push("/dashboard/")
      },
      () => {
        dispatch(
          setToastMessage({
            title:   "Coupon failed",
            message: "This coupon code is invalid or does not exist.",
            icon:    TOAST_FAILURE
          })
        )
        this.context.router.push("/dashboard/")
      }
    )
  }

  getCurrentlyEnrolledProgram = () => {
    const { currentProgramEnrollment, dashboard } = this.props
    if (_.isNil(currentProgramEnrollment) || _.isNil(dashboard)) {
      return undefined
    }
    return dashboard.programs.find(
      program => program.id === currentProgramEnrollment.id
    )
  }

  setConfirmSkipDialogVisibility = bool => {
    const { dispatch } = this.props
    dispatch(setConfirmSkipDialogVisibility(bool))
  }

  setExamEnrollmentDialogVisibility = bool => {
    const { dispatch } = this.props
    dispatch(setExamEnrollmentDialogVisibility(bool))
  }

  addCourseEnrollment = (courseId: string): Promise<*> => {
    const { dispatch } = this.props
    return dispatch(actions.courseEnrollments.post(courseId)).then(
      () => {
        dispatch(fetchDashboard(SETTINGS.user.username, true))
        dispatch(showEnrollPayLaterSuccessMessage(courseId))
      },
      () => {
        dispatch(
          setToastMessage({
            message: "Failed to add course enrollment.",
            icon:    TOAST_FAILURE
          })
        )
      }
    )
  }

  addExamEnrollment = (examCourseId: string): Promise<*> => {
    const { dispatch } = this.props
    return dispatch(actions.examEnrollment.post(examCourseId)).then(
      (result: ExamEnrollmentResponse) => {
        if (!result) {
          return
        }
        window.location = result.url
      },
      () => {
        dispatch(
          setToastMessage({
            message: "Failed to add exam enrollment.",
            icon:    TOAST_FAILURE
          })
        )
      }
    )
  }

  setDocsInstructionsVisibility = bool => {
    const { dispatch } = this.props
    dispatch(setDocsInstructionsVisibility(bool))
  }

  setCouponNotificationVisibility = bool => {
    const { dispatch } = this.props
    dispatch(setCouponNotificationVisibility(bool))
  }

  setRecentlyAttachedCoupon = (coupon: Coupon) => {
    const { dispatch } = this.props
    dispatch(setRecentlyAttachedCoupon(coupon))
  }

  setEnrollCourseDialogVisibility = bool => {
    const { dispatch } = this.props
    dispatch(setEnrollCourseDialogVisibility(bool))
  }

  setEnrollSelectedCourseRun = (run: CourseRun) => {
    const { dispatch } = this.props
    dispatch(setEnrollSelectedCourseRun(run))
  }

  setSelectedExamCouponCourse = (courseId: number) => {
    const { dispatch } = this.props
    dispatch(setSelectedExamCouponCourse(courseId))
  }

  setCalculatePriceDialogVisibility = bool => {
    const { dispatch } = this.props
    dispatch(setCalculatePriceDialogVisibility(bool))
  }

  navigateToProfile = () => {
    this.context.router.push("/learner")
  }

  setShowGradeDetailDialog = (
    open: boolean,
    gradeType: GradeType,
    courseTitle: string
  ) => {
    const { dispatch } = this.props
    if (open) {
      dispatch(showDialog(gradeDetailPopupKey(gradeType, courseTitle)))
    } else {
      dispatch(hideDialog(gradeDetailPopupKey(gradeType, courseTitle)))
    }
  }

  setShowExpandedCourseStatus = (courseId: number) => {
    const { dispatch } = this.props
    dispatch(setShowExpandedCourseStatus(courseId))
  }

  renderCouponDialog() {
    const { programs, ui, coupons, dashboard } = this.props
    const coupon = coupons.recentlyAttachedCoupon
    if (!coupon) {
      return null
    }
    const couponProgram = programs.availablePrograms.find(
      program => program.id === coupon.program_id
    )
    let couponCourse = null
    if (coupon.content_type === COUPON_CONTENT_TYPE_COURSE) {
      const dashboardCouponProgram: Program = (dashboard.programs.find(
        program => program.id === coupon.program_id
      ): any)
      couponCourse = dashboardCouponProgram.courses.find(
        course => course.id === coupon.object_id
      )
    }
    return (
      <CouponNotificationDialog
        coupon={coupon}
        couponProgram={couponProgram}
        couponCourse={couponCourse}
        open={ui.couponNotificationVisibility}
        setDialogVisibility={this.setCouponNotificationVisibility}
      />
    )
  }

  renderExamEnrollmentDialog() {
    const { ui } = this.props
    const program = this.getCurrentlyEnrolledProgram()

    if (!program) {
      return null
    }
    let course = null
    if (ui.selectedExamCouponCourse) {
      course = program.courses.find(
        course => course.id === ui.selectedExamCouponCourse
      )
    }
    if (!course) {
      return null
    }
    return (
      <ExamEnrollmentDialog
        open={ui.examEnrollmentDialogVisibility}
        setVisibility={this.setExamEnrollmentDialogVisibility}
        addExamEnrollment={this.addExamEnrollment}
        course={course}
      />
    )
  }

  renderCourseEnrollmentDialog() {
    const { ui } = this.props
    const program = this.getCurrentlyEnrolledProgram()
    if (!program) {
      return null
    }
    const courseRun = ui.enrollSelectedCourseRun
    if (!courseRun) {
      return null
    }
    const course = program.courses.find(course =>
      R.contains(courseRun.id, R.pluck("id", course.runs))
    )
    if (!course) {
      return null
    }

    return (
      <CourseEnrollmentDialog
        course={course}
        courseRun={courseRun}
        open={ui.enrollCourseDialogVisibility}
        setVisibility={this.setEnrollCourseDialogVisibility}
        addCourseEnrollment={this.addCourseEnrollment}
      />
    )
  }

  renderEdxCacheRefreshErrorMessage() {
    const { dashboard } = this.props
    if (dashboard.isEdxDataFresh) {
      return null
    }
    const email = SETTINGS.support_email

    return (
      <div className="alert-message alert-message-inline">
        <Alert bsStyle="danger">
          <p>
            Sorry, we're unable to update your dashboard at this time, so some
            of the information on this page may not be up to date. <br />
            Please refresh the browser, or check back later.
          </p>
          <p>
            If the error persists, contact{" "}
            <a href={`mailto:${email}`}>{email}</a> for help.
          </p>
        </Alert>
      </div>
    )
  }

  renderErrorMessage = (): React$Element<*> | null => {
    const { dashboard } = this.props
    if (dashboard.errorInfo) {
      return <ErrorMessage errorInfo={dashboard.errorInfo} />
    }
    return null
  }

  renderLearnersInProgramCard(programID: number) {
    const { programLearners } = this.props
    let learnersInProgramCard
    if (
      R.pathSatisfies(
        count => count > 0,
        [programID, "data", "learners_count"],
        programLearners
      )
    ) {
      learnersInProgramCard = (
        <LearnersInProgramCard
          programLearners={programLearners[programID].data}
        />
      )
    }
    return learnersInProgramCard
  }

  addProgramEnrollmentInProgram = (programId: number): Promise<*> => {
    const { dispatch } = this.props
    return dispatch(addProgramEnrollment(programId))
  }

  setEnrollDialogError = (error: ?string): void => {
    const { dispatch } = this.props
    dispatch(setEnrollProgramDialogError(error))
  }

  setEnrollDialogVisibility = (visibility: boolean): void => {
    const { dispatch } = this.props
    dispatch(setEnrollProgramDialogVisibility(visibility))
  }

  setEnrollSelectedProgramById = (programId: ?number): void => {
    const { dispatch } = this.props
    if (programId) {
      dispatch(setEnrollSelectedProgram(programId))
    }
  }

  noProgramSelectedCard = () => {
    const {
      programs,
      ui: {
        enrollProgramDialogError,
        enrollProgramDialogVisibility,
        enrollSelectedProgram
      }
    } = this.props

    return (
      <div>
        <Card shadow={1} className="no-program-card">
          <div>You are not currently enrolled in any programs</div>
          <button
            className="mm-minor-action enroll-wizard-button"
            onClick={() => this.setEnrollDialogVisibility(true)}
          >
            Enroll in a MicroMasters Program
          </button>
          <ProgramEnrollmentDialog
            enrollInProgram={this.addProgramEnrollmentInProgram}
            programs={programs.availablePrograms}
            selectedProgram={enrollSelectedProgram}
            error={enrollProgramDialogError}
            visibility={enrollProgramDialogVisibility}
            setError={this.setEnrollDialogError}
            setVisibility={this.setEnrollDialogVisibility}
            setSelectedProgram={this.setEnrollSelectedProgramById}
            fetchAddStatus={programs.postStatus}
          />
        </Card>
      </div>
    )
  }

  renderPageContent = (): React$Element<*> => {
    const {
      profile: { profile },
      ui
    } = this.props
    const program = this.getCurrentlyEnrolledProgram()

    if (!program) {
      return this.noProgramSelectedCard()
    }

    // Upgrades are discontinued, so coupon prices are empty
    const couponPrices = {
      pricesInclCouponByRun:     new Map(),
      pricesInclCouponByCourse:  new Map(),
      pricesInclCouponByProgram: new Map(),
      pricesExclCouponByProgram: new Map()
    }

    return (
      <div>
        {this.renderEdxCacheRefreshErrorMessage()}
        <h5 className="program-title-dashboard">{program.title}</h5>
        <div className="double-column">
          {this.renderCouponDialog()}
          {this.renderCourseEnrollmentDialog()}
          {this.renderExamEnrollmentDialog()}
          <div className="first-column">
            <DashboardUserCard profile={profile} program={program} />
            <CourseListCard
              email={profile.email}
              program={program}
              couponPrices={couponPrices}
              key={program.id}
              ui={ui}
              addCourseEnrollment={this.addCourseEnrollment}
              openCourseContactDialog={this.openCourseContactDialog}
              setEnrollSelectedCourseRun={this.setEnrollSelectedCourseRun}
              setSelectedExamCouponCourse={this.setSelectedExamCouponCourse}
              setExamEnrollmentDialogVisibility={
                this.setExamEnrollmentDialogVisibility
              }
              setEnrollCourseDialogVisibility={
                this.setEnrollCourseDialogVisibility
              }
              setCalculatePriceDialogVisibility={
                this.setCalculatePriceDialogVisibility
              }
              setShowExpandedCourseStatus={this.setShowExpandedCourseStatus}
              setShowGradeDetailDialog={this.setShowGradeDetailDialog}
              showStaffView={false}
            />
          </div>
          <div className="second-column">
            <ProgressWidget program={program} />
            {this.renderLearnersInProgramCard(program.id)}
          </div>
        </div>
      </div>
    )
  }

  render() {
    const { dashboard } = this.props
    const loaded = isFinishedProcessing(dashboard.fetchStatus)
    const fetchStarted = !_.isNil(dashboard.fetchStatus)
    const noSpinner = dashboard.noSpinner

    const errorMessage = this.renderErrorMessage()
    let pageContent
    if ((_.isNil(errorMessage) && loaded && fetchStarted) || noSpinner) {
      pageContent = this.renderPageContent()
    }

    return (
      <DocumentTitle title="Learner Dashboard | MITx MicroMasters">
        <div className="dashboard">
          <Loader loaded={loaded}>
            {errorMessage}
            {pageContent}
          </Loader>
          <SocialAuthReauthenticateDialog
            invalidBackendCredentials={dashboard.invalidBackendCredentials}
          />
        </div>
      </DocumentTitle>
    )
  }
}

const mapStateToProps = state => {
  let profile = {
    profile: {}
  }
  if (SETTINGS.user && state.profiles[SETTINGS.user.username] !== undefined) {
    profile = state.profiles[SETTINGS.user.username]
  }
  return {
    profile:                  profile,
    dashboard:                getOwnDashboard(state),
    programLearners:          state.programLearners,
    programs:                 state.programs,
    currentProgramEnrollment: state.currentProgramEnrollment,
    ui:                       state.ui,
    email:                    state.email,
    coupons:                  state.coupons
  }
}

export default R.compose(
  connect(mapStateToProps),
  withEmailDialog({
    [COURSE_EMAIL_TYPE]: COURSE_TEAM_EMAIL_CONFIG
  })
)(DashboardPage)
